import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import type { AppRole, PortalRole } from "@/types/domain";

/**
 * 라우트 가드.
 *
 * - 세션 복원 중에는 판정을 미룬다. (복원 전에 리다이렉트하면 새로고침 때마다 로그인 화면이 뜬다)
 * - 미인증이면 로그인 화면으로 보내되 원래 목적지를 `state.from` 으로 넘겨 로그인 후 복귀시킨다.
 * - 인증은 됐지만 역할이 모자라면 리다이렉트하지 않고 접근 불가를 안내한다.
 *   (기사 토큰으로 /admin 을 열었을 때 로그인 화면으로 튕기면 원인을 알 수 없다)
 */

export interface RequireAuthProps {
  /** 허용 역할. 생략하면 인증 여부만 검사한다 */
  roles?: readonly AppRole[];
  /** 생략하면 중첩 라우트(<Outlet/>)를 렌더한다 */
  children?: ReactNode;
}

/** 역할별 기본 진입 화면 */
export function homePathFor(role: AppRole | null): string {
  if (role === "기사") return "/driver/home";
  if (role === "업체") return "/carrier/orders";
  if (role === "청구업체") return "/client/invoices";
  return "/admin/dispatch";
}

/**
 * 그 주체가 **실제로 들 수 있는 역할 전부**.
 *
 * 포털 계정은 한 계정이 운송업체·청구업체 기능을 함께 켤 수 있다(고객 질문 2026-10-15).
 * `role` 은 대표 하나뿐이라 그것만 보면 **둘 다 켠 계정이 한쪽 포털에서 막힌다** —
 * 고객이 겪은 *"청구업체로 만들었더니 송장 제출 메뉴가 없다"* 가 그 증상의 화면 쪽 짝이다.
 */
function effectiveRoles(role: AppRole | null, portalRoles: PortalRole[]): Set<AppRole> {
  const set = new Set<AppRole>(portalRoles);
  if (role) set.add(role);
  return set;
}

/**
 * 어느 로그인 화면으로 보낼지.
 *
 * 요구 역할이 한 종류뿐이면 그 로그인으로, 아니면 지금 경로로 판정한다. 담당자·기사·업체·
 * 청구업체가 각자 다른 로그인 화면을 쓰는데 토큰 슬롯은 한 칸이라, 엉뚱한 화면으로 보내면
 * 로그인해도 다시 튕긴다.
 */
function loginPathFor(roles: readonly AppRole[] | undefined, pathname: string): string {
  const only = (role: AppRole) =>
    roles !== undefined && roles.length > 0 && roles.every((r) => r === role);
  if (only("기사") || pathname.startsWith("/driver")) return "/driver/login";
  if (only("업체") || pathname.startsWith("/carrier")) return "/carrier/login";
  if (only("청구업체") || pathname.startsWith("/client")) return "/client/login";
  return "/login";
}

/** 레이아웃 안쪽에서도 렌더될 수 있어 화면 높이를 강제하지 않는다 */
function Restoring() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center text-sm text-gray-400">
      불러오는 중
    </div>
  );
}

export default function RequireAuth({ roles, children }: RequireAuthProps) {
  const { isAuthenticated, isRestoring, role, portalRoles, logout } = useAuth();
  const location = useLocation();

  if (isRestoring) return <Restoring />;

  if (!isAuthenticated) {
    return (
      <Navigate
        to={loginPathFor(roles, location.pathname)}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  const held = effectiveRoles(role, portalRoles);
  if (roles && roles.length > 0 && !roles.some((r) => held.has(r))) {
    const wantsDriver = roles.every((r) => r === "기사");
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-6">
        <p className="text-sm text-gray-900">접근 권한이 없습니다.</p>
        <p className="mt-2 text-xs text-gray-500">
          이 화면은 {roles.join(" · ")} 권한이 필요합니다. 현재 권한은 {role ?? "없음"} 입니다.
        </p>
        {/*
         * 담당자가 기사 화면을 열었을 때 나가는 길이 `처음 화면으로` 하나뿐이면 갇힌다.
         * 토큰은 한 칸이라 두 역할을 동시에 들 수 없으므로, 바꿔 타는 버튼을 여기에 둔다
         * — 기사앱이 고장 난 것으로 오해받은 원인이 이 자리였다(PO 피드백 2026-08-26).
         */}
        {wantsDriver && (
          <button
            type="button"
            onClick={() => {
              void logout().finally(() => {
                window.location.href = "/driver/login";
              });
            }}
            className="mt-6 border border-gray-300 px-4 py-2 text-xs text-gray-900 transition-colors hover:border-gray-900"
          >
            기사로 로그인
          </button>
        )}
        <a
          href={homePathFor(role)}
          className="mt-4 text-xs text-gray-500 underline underline-offset-4 transition-colors hover:text-gray-900"
        >
          처음 화면으로
        </a>
      </div>
    );
  }

  return <>{children ?? <Outlet />}</>;
}

/**
 * 로그인 화면 전용 가드 — 이미 **그 로그인 화면의 역할로** 인증됐으면 자기 홈으로 보낸다.
 *
 * `for` 를 주면 그 역할일 때만 튕긴다. 담당자로 로그인한 채 `/driver/login` 을 열면
 * 예전에는 `/admin/dispatch` 로 되돌아가 **기사 로그인 폼에 도달할 방법이 없었다**
 * (PO 피드백 2026-08-26 — 기사앱이 고장 난 것으로 보였다). 역할이 다르면 폼을 보여 주고,
 * 세션이 바뀐다는 사실은 로그인 화면이 글자로 알린다.
 */
export function RedirectIfAuthenticated({
  children,
  roles,
}: {
  children: ReactNode;
  /** 이 역할들로 이미 인증된 경우에만 홈으로 보낸다. 생략하면 인증된 전원 */
  roles?: readonly AppRole[];
}) {
  const { isAuthenticated, isRestoring, role, portalRoles } = useAuth();

  if (isRestoring) return <Restoring />;
  const held = effectiveRoles(role, portalRoles);
  if (isAuthenticated && (roles === undefined || roles.some((r) => held.has(r)))) {
    return <Navigate to={homePathFor(role)} replace />;
  }

  return <>{children}</>;
}
